/* eslint-disable react/prop-types */
/* eslint-disable simple-import-sort/imports */
import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { styled } from 'styled-components';

import LogoFox from 'assets/icons/logo.svg';
import { APP_URL } from 'constants/index';

import { CALENDLY_URL, v2 } from '../v2';

const NAV_LINKS = [
  { label: 'Features', target: 'features' },
  // { label: 'Stimmen', target: 'stimmen' }, // vorübergehend deaktiviert – Sektion kommt in neuer Version zurück
  { label: 'FAQ', target: 'faq' },
  { label: 'Kontakt', target: 'kontakt' },
];

const Bar = styled(motion.header)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  transition:
    background 0.4s ease,
    border-color 0.4s ease,
    backdrop-filter 0.4s ease;
  border-bottom: 1px solid
    ${({ $scrolled }) => ($scrolled ? v2.border : 'transparent')};
  background: ${({ $scrolled }) =>
    $scrolled ? 'rgba(5, 9, 15, 0.72)' : 'transparent'};
  backdrop-filter: ${({ $scrolled }) =>
    $scrolled ? 'blur(18px)' : 'blur(0px)'};
`;

const BarInner = styled.div`
  max-width: ${v2.container};
  margin: 0 auto;
  padding: 0 24px;
  height: 76px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
`;

const Brand = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  color: ${v2.text};

  img {
    width: 40px;
    height: 40px;
  }

  /* Text group is centered on the icon; FOXI and "abrechnung"
     share a baseline within it. */
  .brand-text {
    display: inline-flex;
    align-items: baseline;
    gap: 12px;
  }

  span {
    font-family: ${v2.fontDisplay};
    font-size: 20px;
    font-weight: 800;
    letter-spacing: 2px;
  }

  small {
    font-size: 13px;
    font-weight: 400;
    color: ${v2.textMuted};
    letter-spacing: 0;
    margin-left: -6px;
  }
`;

const Links = styled.nav`
  display: flex;
  align-items: center;
  gap: 6px;

  @media (max-width: 920px) {
    display: none;
  }
`;

const NavLink = styled.button`
  font-family: ${v2.fontBody};
  font-size: 15px;
  font-weight: 600;
  color: ${v2.textMuted};
  padding: 10px 16px;
  border-radius: 100px;
  transition:
    color 0.25s ease,
    background 0.25s ease;

  &:hover {
    color: ${v2.text};
    background: ${v2.surface};
  }
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const LoginLink = styled.a`
  font-family: ${v2.fontBody};
  font-size: 15px;
  font-weight: 600;
  color: ${v2.textMuted};
  padding: 10px 16px;
  border-radius: 100px;
  transition: color 0.25s ease;

  &:hover {
    color: ${v2.text};
  }

  @media (max-width: 480px) {
    display: none;
  }
`;

const CtaLink = styled.a`
  font-family: ${v2.fontBody};
  font-size: 15px;
  font-weight: 700;
  color: #fff;
  padding: 11px 22px;
  border-radius: 100px;
  background: ${v2.gradientBtn};
  box-shadow: 0 4px 20px rgba(30, 150, 252, 0.35);
  transition:
    box-shadow 0.25s ease,
    transform 0.25s ease;

  &:hover {
    box-shadow: 0 6px 28px rgba(30, 150, 252, 0.55);
    transform: translateY(-1px);
  }

  @media (max-width: 920px) {
    display: none;
  }
`;

const Burger = styled.button`
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid ${v2.border};
  background: ${v2.surface};
  align-items: center;

  span {
    display: block;
    width: 16px;
    height: 2px;
    border-radius: 2px;
    background: ${v2.text};
    transition:
      transform 0.3s ease,
      opacity 0.3s ease;
  }

  ${({ $open }) =>
    $open &&
    `
    span:nth-child(1) { transform: translateY(7px) rotate(45deg); }
    span:nth-child(2) { opacity: 0; }
    span:nth-child(3) { transform: translateY(-7px) rotate(-45deg); }
  `}

  @media (max-width: 920px) {
    display: flex;
  }
`;

const MobileMenu = styled(motion.div)`
  position: fixed;
  inset: 0;
  z-index: 99;
  background: rgba(5, 9, 15, 0.92);
  backdrop-filter: blur(24px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const MobileLink = styled(motion.button)`
  font-family: ${v2.fontDisplay};
  font-size: clamp(1.8rem, 7vw, 2.6rem);
  font-weight: 700;
  color: ${v2.text};
  padding: 8px 24px;

  &:hover {
    color: ${v2.blue};
  }
`;

const MobileCta = styled(motion.a)`
  margin-top: 24px;
  font-family: ${v2.fontBody};
  font-size: 17px;
  font-weight: 700;
  color: #fff;
  padding: 16px 36px;
  border-radius: 100px;
  background: ${v2.gradientBtn};
`;

/**
 * Fixed site header. `links` lets other pages that share this design (e.g.
 * /goodie) provide their own in-page targets; the start page keeps NAV_LINKS.
 */
const Nav = ({ onNavigate, links = NAV_LINKS }) => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const go = (target) => {
    setOpen(false);
    onNavigate(target);
  };

  return (
    <>
      <Bar
        $scrolled={scrolled}
        initial={{ y: -90 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.8, ease: v2.ease, delay: 0.2 }}
      >
        <BarInner>
          <Brand
            to="/"
            onClick={(event) => {
              event.preventDefault();
              go('top');
            }}
          >
            <img src={LogoFox} alt="FOXI" />
            <span className="brand-text">
              <span>FOXI</span>
              <small>abrechnung</small>
            </span>
          </Brand>
          <Links>
            {links.map((link) => (
              <NavLink key={link.target} onClick={() => go(link.target)}>
                {link.label}
              </NavLink>
            ))}
          </Links>
          <Actions>
            <LoginLink href={APP_URL} target="_blank" rel="noreferrer">
              Login
            </LoginLink>
            <CtaLink href={CALENDLY_URL} target="_blank" rel="noreferrer">
              Termin vereinbaren
            </CtaLink>
            <Burger
              aria-label="Menü"
              $open={open}
              onClick={() => setOpen((value) => !value)}
            >
              <span />
              <span />
              <span />
            </Burger>
          </Actions>
        </BarInner>
      </Bar>
      <AnimatePresence>
        {open && (
          <MobileMenu
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            {links.map((link, index) => (
              <MobileLink
                key={link.target}
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.08 * index, duration: 0.5 }}
                onClick={() => go(link.target)}
              >
                {link.label}
              </MobileLink>
            ))}
            <MobileCta
              href={CALENDLY_URL}
              target="_blank"
              rel="noreferrer"
              onClick={() => setOpen(false)}
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.45, duration: 0.5 }}
            >
              Termin vereinbaren
            </MobileCta>
          </MobileMenu>
        )}
      </AnimatePresence>
    </>
  );
};

export default Nav;
